<script setup lang="ts">
import { computed, inject, onMounted, ref, shallowRef, useTemplateRef } from "vue";
import { useRoute } from "vue-router";

import ColorBadge from "@/components/ui/ColorBadge.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import { extractIdFromUrn, getReportName, ReportUrnPrefix, type EstimatorReportDto } from "@/dto";
import { raiseErrorKey } from "@/injectionKeys";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { ProjectDetailSections, RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { useWorkspaceFetcher } from "@/services/workspaceFetcher";
import EstimatorReport from "@/views/reports/EstimatorReport.vue";
import ReportDrawer from "@/views/reports/ReportDrawer.vue";

const api = useApi();
const route = useRoute();

const raiseError = inject(raiseErrorKey);

const report = shallowRef<EstimatorReportDto>();
const isLoading = ref(true);
const reportDrawer = useTemplateRef<InstanceType<typeof ReportDrawer>>("reportDrawer");

const workspacePublicId = computed(() => route.params.workspacePublicId as string);
const projectName = computed(() => route.params.projectName as string);
const reportId = computed(() => route.params.reportId as string);

const workspace = useWorkspaceFetcher(workspacePublicId.value);

async function fetchExperiment() {
  isLoading.value = true;
  const r = await api.fetchEstimatorReport(
    workspacePublicId.value,
    projectName.value,
    parseInt(reportId.value)
  );
  if (r === null) {
    raiseError?.(404);
  } else {
    report.value = r;
    isLoading.value = false;
  }
}

async function openCrossValidationReportInDrawer(urn: string) {
  const reportId = extractIdFromUrn(urn);
  await reportDrawer.value?.openCrossValidationReport(reportId);
}

const parentCrossValidationReportUrn = computed(() => {
  if (report.value && "parents_urns" in report.value) {
    return (report.value as EstimatorReportDto).parents_urns.find((urn) =>
      urn.startsWith(ReportUrnPrefix.CrossValidation)
    );
  }
  return undefined;
});

onMounted(async () => {
  await fetchExperiment();
});
</script>

<template>
  <DefaultPageLayout
    :breadcrumb-items="[
      {
        label: workspace?.name ?? workspacePublicId,
        to: { name: RouteNames.Projects, params: { workspacePublicId } },
        icon: 'icon-masonry',
      },
      {
        label: projectName,
        to: {
          name: RouteNames.ProjectDetail,
          params: {
            workspacePublicId,
            projectName,
            section: ProjectDetailSections.EstimatorReports,
          },
        },
        icon: 'icon-punch-card',
        iconColor: 'var(--color-purple-500)',
      },
      {
        label: 'Estimators',
        to: {
          name: RouteNames.ProjectDetail,
          params: {
            workspacePublicId,
            projectName,
            section: ProjectDetailSections.EstimatorReports,
          },
        },
      },
      {
        label: report ? getReportName(report) : '...',
      },
    ]"
  >
    <Transition name="fade" mode="out-in">
      <div v-if="isLoading" class="loading">
        <SkoreSpinner />
      </div>

      <div v-else-if="report !== undefined" class="estimator-report-view">
        <ColorBadge v-if="parentCrossValidationReportUrn" class="parent-info" color="blue" size="l">
          <i class="icon icon-info-circle-2" />
          This estimator is related to
          <i class="icon icon-playground" />
          <span
            class="link"
            @click="openCrossValidationReportInDrawer(parentCrossValidationReportUrn)"
          >
            {{ parentCrossValidationReportUrn }}
          </span>
        </ColorBadge>

        <EstimatorReport :report="report" :use-anchor="true">
          <template #summaryPrelude>
            <div class="back">
              <RouterLink
                :to="{
                  name: RouteNames.ProjectDetail,
                  params: {
                    workspacePublicId: workspacePublicId,
                    projectName: projectName,
                    section: ProjectDetailSections.EstimatorReports,
                  },
                }"
                class="text-base-medium"
              >
                <i class="icon icon-arrow-left" /> Estimators
              </RouterLink>
            </div>
          </template>

          <template #contentPrelude>
            <div class="content-prelude text-base-medium">&nbsp;</div>
          </template>
        </EstimatorReport>
      </div>
    </Transition>
  </DefaultPageLayout>

  <ReportDrawer
    ref="reportDrawer"
    :workspace-public-id="workspacePublicId"
    :project-name="projectName"
  >
    <template #title>Parent cross-validation report</template>
  </ReportDrawer>
</template>

<style scoped>
.loading {
  margin-top: 40dvh;
  text-align: center;
}

.estimator-report-view {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;

  .back,
  .content-prelude {
    padding: var(--numbers-16) 0;
  }

  .back {
    color: var(--content-secondary);
    cursor: pointer;
    white-space: nowrap;

    a,
    a:visited {
      color: inherit;
      text-decoration: none;
    }

    .icon {
      margin-right: var(--numbers-8);
      vertical-align: text-top;
    }
  }

  .parent-info {
    width: 100dvw;
    margin-left: calc(var(--page-padding) * -1);
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    text-align: left;

    .icon {
      vertical-align: middle;
    }

    .link {
      cursor: pointer;
      text-decoration: underline;
    }
  }
}
</style>
